import { extractImageFrames, clampTo } from '../../tools';
import type { GeneralModelConfig } from '../../general';

export const seedanceConfigs: Record<string, GeneralModelConfig> = {
    'seedance-2.5': {
        apiPath: '/v1/videos/generations',
        pollApiPath: '/v1/videos/tasks/{taskId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 30);
            const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 10);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 10);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'seedance-2.5',
                    prompt: prompt,
                    ratio: 'auto',
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p', '1080p'] as const, '720p'),
                    // 首帧 URL
                    file_paths: [firstImage],
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'first_last_frames',
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'seedance-2.5',
                    prompt: prompt,
                    ratio: 'auto',
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p', '1080p'] as const, '720p'),
                    // 首尾帧 URL
                    file_paths: [firstImage, lastImage || firstImage],
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'first_last_frames',
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'seedance-2.5',
                    prompt: prompt,
                    ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p', '1080p'] as const, '720p'),
                    // 素材
                    ...Object.fromEntries(images.map((url, i) => [`image_file_${i + 1}`, url])),
                    ...Object.fromEntries(videos.map((url, i) => [`video_file_${i + 1}`, url])),
                    ...Object.fromEntries(audios.map((url, i) => [`audio_file_${i + 1}`, url])),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'omni_reference',
                };
            // 文生视频模式
            } else {
                return {
                    model: 'seedance-2.5',
                    prompt: prompt,
                    resolution: clampTo(quality || '720p', ['480p', '720p', '1080p'] as const, '720p'),
                    ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'omni_reference',
                };
            }
        },
    },
    'seedance-2.5-long': {
        apiPath: '/v1/videos/generations',
        pollApiPath: '/v1/videos/tasks/{taskId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality} = params;
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 30);
            const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 10);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 10);
            return {
                model: 'jmg-video-seedance-2.5-long',
                prompt: prompt,
                ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                duration: clampTo(duration || 30, [30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127, 128, 129, 130, 131, 132, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151, 152, 153, 154, 155, 156, 157, 158, 159, 160, 161, 162, 163, 164, 165, 166, 167, 168, 169, 170, 171, 172, 173, 174, 175, 176, 177, 178, 179, 180] as const, 30),
                resolution: clampTo(quality || '720p', ['480p', '720p', '1080p'] as const, '720p'),
                // 素材
                ...Object.fromEntries(images.map((url, i) => [`image_file_${i + 1}`, url])),
                ...Object.fromEntries(videos.map((url, i) => [`video_file_${i + 1}`, url])),
                ...Object.fromEntries(audios.map((url, i) => [`audio_file_${i + 1}`, url])),
                // system
                functionMode: 'omni_reference',
            };
        },
    },
    'seedance-2.0': {
        apiPath: '/v1/videos/generations',
        pollApiPath: '/v1/videos/tasks/{taskId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 3);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 3);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'seedance-2.0',
                    prompt: prompt,
                    ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p', '1080p', '4k'] as const, '720p'),
                    // 首帧 URL
                    file_paths: [firstImage],
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'first_last_frames',
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'seedance-2.0',
                    prompt: prompt,
                    ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p', '1080p', '4k'] as const, '720p'),
                    // 首尾帧 URL
                    file_paths: [firstImage, lastImage || firstImage],
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'first_last_frames',
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'seedance-2.0',
                    prompt: prompt,
                    ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p', '1080p', '4k'] as const, '720p'),
                    // 素材
                    ...Object.fromEntries(images.map((url, i) => [`image_file_${i + 1}`, url])),
                    ...Object.fromEntries(videos.map((url, i) => [`video_file_${i + 1}`, url])),
                    ...Object.fromEntries(audios.map((url, i) => [`audio_file_${i + 1}`, url])),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'omni_reference',
                };
            // 文生视频模式
            } else {
                return {
                    model: 'seedance-2.0',
                    prompt: prompt,
                    resolution: clampTo(quality || '720p', ['480p', '720p', '1080p', '4k'] as const, '720p'),
                    ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'omni_reference',
                };
            }
        },
    },
    'seedance-2.0-fast': {
        apiPath: '/v1/videos/generations',
        pollApiPath: '/v1/videos/tasks/{taskId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 3);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 3);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'seedance-2.0-fast',
                    prompt: prompt,
                    ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p'] as const, '720p'),
                    // 首帧 URL
                    file_paths: [firstImage],
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'first_last_frames',
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'seedance-2.0-fast',
                    prompt: prompt,
                    ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p'] as const, '720p'),
                    // 首尾帧 URL
                    file_paths: [firstImage, lastImage || firstImage],
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'first_last_frames',
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'seedance-2.0-fast',
                    prompt: prompt,
                    ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p'] as const, '720p'),
                    // 素材
                    ...Object.fromEntries(images.map((url, i) => [`image_file_${i + 1}`, url])),
                    ...Object.fromEntries(videos.map((url, i) => [`video_file_${i + 1}`, url])),
                    ...Object.fromEntries(audios.map((url, i) => [`audio_file_${i + 1}`, url])),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'omni_reference',
                };
            // 文生视频模式
            } else {
                return {
                    model: 'seedance-2.0-fast',
                    prompt: prompt,
                    resolution: clampTo(quality || '720p', ['480p', '720p'] as const, '720p'),
                    ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'omni_reference',
                };
            }
        },
    },
    'seedance-2.0-mini': {
        apiPath: '/v1/videos/generations',
        pollApiPath: '/v1/videos/tasks/{taskId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 3);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 3);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'seedance-2.0-mini',
                    prompt: prompt,
                    ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p'] as const, '720p'),
                    // 首帧 URL
                    file_paths: [firstImage],
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'first_last_frames',
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'seedance-2.0-mini',
                    prompt: prompt,
                    ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p'] as const, '720p'),
                    // 首尾帧 URL
                    file_paths: [firstImage, lastImage || firstImage],
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'first_last_frames',
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'seedance-2.0-mini',
                    prompt: prompt,
                    ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '720p', ['480p', '720p'] as const, '720p'),
                    // 素材
                    ...Object.fromEntries(images.map((url, i) => [`image_file_${i + 1}`, url])),
                    ...Object.fromEntries(videos.map((url, i) => [`video_file_${i + 1}`, url])),
                    ...Object.fromEntries(audios.map((url, i) => [`audio_file_${i + 1}`, url])),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'omni_reference',
                };
            // 文生视频模式
            } else {
                return {
                    model: 'seedance-2.0-mini',
                    prompt: prompt,
                    resolution: clampTo(quality || '720p', ['480p', '720p'] as const, '720p'),
                    ratio: clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    // system
                    functionMode: 'omni_reference',
                };
            }
        },
    },
};